import { useCallback, useEffect, useRef, useState } from 'react';

import {
  consumeImportEvents,
  createBulkUserImport,
  fetchBulkUserImport,
} from '../services/bulkUserImportService';

export const POLL_INTERVAL = 2000;

export const PHASES = {
  upload: 'upload',
  processing: 'processing',
  summary: 'summary',
};

/**
 * The upload and its progress, for the Create Multiple Users wizard (PD-4066).
 *
 * The server answers the upload with 202 and an id, so "how did it go" is a
 * separate question asked on a timer. The polling stops when the import reaches
 * a terminal status, when the hook unmounts, or when the wizard is reset — the
 * last of which matters because the admin can close the wizard mid-import and
 * the page behind it keeps its own watch (see useUserEvents).
 */
export default function useBulkUserImport({
  // Injectable so tests can drive the poll on real timers. React 16's async
  // `act` flushes through setImmediate, which Jest's fake timers also fake, so
  // faking the clock here would deadlock the test rather than speed it up.
  pollInterval = POLL_INTERVAL,
} = {}) {
  const [phase, setPhase] = useState(PHASES.upload);
  const [importRecord, setImportRecord] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Held in a ref as well as in state: the interval callback closes over the
  // value it was created with, and re-creating the interval on every poll would
  // restart the clock each time.
  const timer = useRef(null);
  const isMounted = useRef(true);
  const inFlight = useRef(false);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      isMounted.current = false;
      stopPolling();
    },
    [stopPolling],
  );

  const poll = useCallback(
    (id) => {
      stopPolling();

      timer.current = setInterval(async () => {
        // A poll that outlives the interval would otherwise overlap the next
        // one, and two answers could arrive out of order — the older one
        // overwriting the newer, or both racing to stop the timer. Skipping a
        // tick costs nothing: the run continues on the server either way.
        if (inFlight.current) return;
        inFlight.current = true;

        try {
          const next = await fetchBulkUserImport(id);
          if (!isMounted.current) return;

          setImportRecord(next);

          if (next.isComplete) {
            stopPolling();
            setPhase(PHASES.summary);
            // The outcome is about to be on screen, so spend this import's
            // completion event rather than letting the page raise a toast
            // saying the same thing over the top of it. Safe to do here even
            // though the worker publishes the event and writes the terminal
            // status in one transaction: seeing the status means the event
            // exists.
            await consumeImportEvents(id);
          }
        } catch {
          // A poll that fails is not an import that failed — the run continues
          // on the server, and the next tick asks again. Giving up here would
          // strand the wizard on the processing panel for a blip.
        } finally {
          inFlight.current = false;
        }
      }, pollInterval);
    },
    [pollInterval, stopPolling],
  );

  const upload = useCallback(
    async (file) => {
      setIsUploading(true);
      setUploadError(null);

      try {
        const created = await createBulkUserImport(file);
        if (!isMounted.current) return;

        setImportRecord(created);
        setPhase(PHASES.processing);
        poll(created.id);
      } catch (error) {
        if (!isMounted.current) return;

        // The server's own words where it has them — it is the only side that
        // knows the size limit was exceeded or the file was not really a CSV.
        setUploadError(error.fieldErrors?.original_file?.[0] || null);
        setPhase(PHASES.upload);
      } finally {
        if (isMounted.current) setIsUploading(false);
      }
    },
    [poll],
  );

  const reset = useCallback(() => {
    stopPolling();
    setPhase(PHASES.upload);
    setImportRecord(null);
    setUploadError(null);
    setIsUploading(false);
  }, [stopPolling]);

  return {
    importRecord,
    isUploading,
    phase,
    reset,
    upload,
    uploadError,
  };
}
